import React from 'react';
import { PasswordInput, PasswordStrength } from '../';

interface StepAccountProps {
  firstName: string;
  lastName: string;
  password: string;
  phone: string;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  onContinue: () => void;
  onBack: () => void;
}

export const StepAccount: React.FC<StepAccountProps> = ({
  firstName,
  lastName,
  password,
  phone,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  onPhoneChange,
  onContinue,
  onBack,
}) => {
  const passwordValid = password.length >= 8 && /[a-z]/.test(password) && /\d/.test(password);
  const canContinue = firstName.trim() && lastName.trim() && passwordValid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canContinue) onContinue();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Create your account
        </h1>
        <p className="text-gray-500 mb-8">
          Tell us a bit about yourself
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name fields side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => onFirstNameChange(e.target.value)}
                required
                placeholder="John"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => onLastNameChange(e.target.value)}
                required
                placeholder="Doe"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <PasswordInput
              label="Password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              required
              placeholder="Create a password"
            />
            <PasswordStrength password={password} />
          </div>

          {/* Phone (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone number <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder="+961 XX XXX XXX"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={!canContinue}
            className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base"
          >
            Continue
          </button>
        </form>
      </div>
    </div>
  );
};

export default StepAccount;
